import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker } from '@synergy/components';
import { PaloField } from '@synergy/components-palo';
import { useFormikContext } from 'formik';

import { PaloDeliveryDateControl } from '../../../../../common/components/palo/palo-delivery-date-control';
import { PaloFormikField } from '../../../../../common/components/palo/palo-formik-field';
import { PaloSendOption } from '../../../../../common/components/palo/palo-send-option';
import { PaloTextControl } from '../../../../../common/components/palo/palo-text-control';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import {
    PurchaseOrderDetailOverviewFormValues,
    purchaseOrderDetailOverviewFieldNames as fieldNames,
} from '../models/purchase-order-detail-form-values';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

interface PaloPurchaseOrderHeadOrderProps {
    readonly?: boolean;
}

/**
 * The order's own data in the head grid: title, date, person in charge and
 * when to deliver. Whether completing also sends it sits beside the main
 * action (#2251).
 */
export const PaloPurchaseOrderHeadOrder = ({ readonly }: PaloPurchaseOrderHeadOrderProps) => {
    const contacts = useAppSelector(state => state.contacts.flat);
    const formik = useFormikContext<PurchaseOrderDetailOverviewFormValues>();

    return (
        <>
            {!readonly && (
                <PaloSendOption
                    name={fieldNames.sendPurchaseOrder}
                    defaultValue={true}
                    label={<FormattedMessage id="app.purchaseOrders.detail.overview.sendToSupplier" />}
                />
            )}
            {/* No autoFocus on the title. The form mounts before the document
                arrives, so it fired on every saved purchase order too: the
                cursor sat in the title unasked, stray keystrokes edited it, and
                the single-letter shortcuts stayed silent because a field held
                the focus. */}
            <PaloTextControl
                name={fieldNames.title}
                label={<FormattedMessage id="app.label.title" />}
                required
                area="title"
                disabled={readonly}
            />
            <PaloField
                label={
                    <>
                        <FormattedMessage id="app.label.orderDate" /> *
                    </>
                }
                name={fieldNames.orderDate}
                area="orderDate"
            >
                <FormikPoolDatePicker name={fieldNames.orderDate} readOnly={readonly} />
            </PaloField>
            <PaloFormikField<PartialContact>
                name={fieldNames.responsibleContact}
                label={<FormattedMessage id="app.common.responsibleContact" />}
                required
                area="responsibleContact"
            >
                {({ field, hasError }) => (
                    <PoolContactAutocompleteList
                        items={contacts}
                        label={<></>}
                        onSelected={value => formik.setFieldValue(field.name, value)}
                        required={false}
                        error={hasError}
                        readOnly={readonly}
                        {...field}
                    />
                )}
            </PaloFormikField>
            <PaloField
                label={<FormattedMessage id="app.label.deliveryDate" />}
                name={[fieldNames.deliveryDateType, fieldNames.deliveryDate]}
                area="deliveryDate"
            >
                <PaloDeliveryDateControl
                    typeName={fieldNames.deliveryDateType}
                    dateName={fieldNames.deliveryDate}
                    readonly={readonly}
                />
            </PaloField>
        </>
    );
};
